import { Trans, useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import cadastroIngredientesService from "features/cadastroIngredientes/cadastroIngredientesService";
import ConfirmationModal from "features/shared/components/confirmationModal";
import React, { useEffect, useRef, useState } from "react";
import { getUserLoginFromStorage } from "shared/storageUtils";
import shortid from "shortid";

import {
  Button,
  FormControl,
  Paper,
  TextField,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { createStyles } from "@mui/material";

import history from "../../../app/History";
import {
  filterFormulaMemoize,
  INCLUIR_FORMULAS_DELETADAS,
} from "../../formulaUtils";
import FormulacaoModeloImpressao from "../formulationModal/components/formulacaoModeloImpressao";
import FormulationModalContainer from "../formulationModal/formulationModalContainer";
import FormulacaoSemItems from "./components/formulacaoSemItems";
import ListaFormulas from "./components/listaFormulas";

interface Ingrediente {
  IdIngrediente: number;
  OperacaoIngrediente: {
    Ativo: boolean;
  };
}

interface Formula {
  Nome: string;
  Excluido: boolean;
  Ingrediente: Ingrediente[];
}

interface ConfirmationModalState {
  showModal: boolean;
  id: number | null;
  descricao: string | null;
  ingredientes: Ingrediente[];
}

interface ListagemFormulacaoPageProps extends WithStyles<typeof styles> {
  formulasFazenda: Formula[];
  exibirCreep: boolean;
  exibirSemiConfinamento: boolean;
  fazendaSelecionada: number;
  obterFormulasFazenda: (param: boolean) => Promise<void>;
  desexcluirFormula: (
    id: number,
    ingredientes?: Ingrediente[]
  ) => Promise<void>;
  excluirFormula: (id: number) => Promise<void>;
}

const ListagemFormulacaoPage: React.FC<ListagemFormulacaoPageProps> = ({
  classes,
  formulasFazenda,
  exibirCreep,
  exibirSemiConfinamento,
  fazendaSelecionada,
  obterFormulasFazenda,
  desexcluirFormula,
  excluirFormula,
}) => {
  const { t } = useTranslations();
  const [open, setOpen] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [estadoSelecionado, setEstadoSelecionado] = useState(1);
  const [formula, setFormula] = useState<Formula | null>(null);
  const [capacidadeMisturador, setCapacidadeMisturador] = useState("");
  const [quantidadeTotal, setQuantidadeTotal] = useState(0);
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationModalState>({
      showModal: false,
      id: null,
      descricao: null,
      ingredientes: [],
    });

  const componentRef = useRef<any>(null);

  const OPCOES_SELECT_INPUT = [
    { value: 0, label: t("common:all-fem").toUpperCase() },
    { value: 1, label: t("common:status.active-plural-fem").toUpperCase() },
    { value: 2, label: t("common:status.inactive-plural-fem").toUpperCase() },
  ];

  useEffect(() => {
    obterFormulasFazenda(INCLUIR_FORMULAS_DELETADAS);
  }, [obterFormulasFazenda]);

  const handleEstado = (value: number) => {
    setEstadoSelecionado(value);
  };

  const cadastroFormulacao = () => {
    const url = window.location.pathname + "/edicao-formulacao";
    history.push(url);
  };

  const editarFormulacao = (formula: Formula) => {
    const url = window.location.pathname + "/edicao-formulacao";
    history.push({
      pathname: url,
      state: { formula: formula },
    });
  };

  const closeModal = () => {
    setConfirmationModal({
      showModal: false,
      id: null,
      descricao: null,
      ingredientes: [],
    });
  };

  const temIngredienteInativo = (formula: Formula): boolean => {
    const ingredientesFormula = formula.Ingrediente;
    if (ingredientesFormula && ingredientesFormula.length > 0) {
      return ingredientesFormula.some(
        (ingrediente) => ingrediente.OperacaoIngrediente.Ativo === false
      );
    }
    return false;
  };

  const activateIngredientAndFormula = async () => {
    const nomeUsuario = getUserLoginFromStorage();
    const arrayIngredientes = confirmationModal.ingredientes;
    for await (const ingrediente of arrayIngredientes) {
      if (ingrediente.OperacaoIngrediente.Ativo === false) {
        await cadastroIngredientesService.ativaIngrediente(
          ingrediente.IdIngrediente,
          nomeUsuario
        );
      }
    }
    await desexcluirFormula(
      confirmationModal.id!,
      confirmationModal.ingredientes
    );
    await obterFormulasFazenda(INCLUIR_FORMULAS_DELETADAS);
    closeModal();
  };

  const ativaDesativaFormulacao = async (
    formula: Formula,
    idFormula: number,
    excluido: boolean
  ) => {
    if (excluido) {
      const formulaTemIngredienteInativo = temIngredienteInativo(formula);
      if (formulaTemIngredienteInativo === true) {
        setConfirmationModal({
          showModal: true,
          id: idFormula,
          descricao: formula.Nome,
          ingredientes: formula.Ingrediente,
        });
      } else {
        await desexcluirFormula(idFormula);
      }
    } else {
      await excluirFormula(idFormula);
    }
    await obterFormulasFazenda(INCLUIR_FORMULAS_DELETADAS);
  };

  const formartModalText = (Descricao: string) => (
    <span>
      <Trans
        i18nKey="formulation:list.inactivate-message"
        components={{ span: <span className={classes.formula} /> }}
        values={{ description: Descricao }}
      />
    </span>
  );

  const alterarStatusModal = (formula?: Formula) => {
    if (formula) {
      setFormula(formula);
      setOpen(true);
    } else {
      setFormula(null);
      setOpen(false);
    }
  };

  let formulacoesFiltradas = filterFormulaMemoize(formulasFazenda, filtro);
  formulacoesFiltradas = formulacoesFiltradas.filter((v) => {
    if (estadoSelecionado === 0) return true;
    else if (estadoSelecionado === 1) return !v.Excluido;
    else return v.Excluido;
  });

  const existeFormulacao =
    formulacoesFiltradas && formulacoesFiltradas.length > 0;

  return (
    <>
      <Paper square className={classes.root}>
        <Grid item className={classes.containerPage}>
          <Typography className={classes.titulo} gutterBottom>
            {t("formulation:formulations")}
          </Typography>
          <div className={classes.linhaFiltro}>
            <FormControl className={classes.formContainer}>
              <TextField
                id="inputFiltro"
                autoFocus
                label={t("formulation:list.search-label")}
                className={classes.inputFiltro}
                onChange={(event) => setFiltro(event.target.value)}
              />
            </FormControl>
            <div className={classes.selectInput}>
              <SelectDropdown
                id="categorias-select"
                label={{ text: t("formulation:list.status-label") }}
                options={OPCOES_SELECT_INPUT}
                value={estadoSelecionado}
                onChange={handleEstado}
              />
            </div>
          </div>
          <Button
            id="botaoSalvar"
            className={classes.botaoSalvar}
            variant="contained"
            color="primary"
            onClick={cadastroFormulacao}
          >
            {t("formulation:new-formulation")}
          </Button>
          {existeFormulacao ? (
            <Grid item>
              <ListaFormulas
                key={shortid.generate()}
                listaFormulas={formulacoesFiltradas}
                editarFormulacao={editarFormulacao}
                ativaDesativaFormulacao={ativaDesativaFormulacao}
                alterarStatusModal={alterarStatusModal}
              />
            </Grid>
          ) : (
            <FormulacaoSemItems />
          )}
        </Grid>
      </Paper>
      {confirmationModal.showModal && (
        <ConfirmationModal
          onSubmit={activateIngredientAndFormula}
          onCancel={closeModal}
          onClose={closeModal}
          title={t("formulation:list.activate-title")}
          submitButtonTitle={t("formulation:list.yes-activate")}
          text={formartModalText(confirmationModal.descricao!)}
        />
      )}
      <FormulationModalContainer
        open={open}
        formula={formula}
        alterarStatusModal={() => alterarStatusModal()}
        onBackdropClick={() => alterarStatusModal()}
        onClickAway={() => alterarStatusModal()}
        handlerCapacidade={setCapacidadeMisturador}
        componentRef={componentRef.current}
        handlerFormula={setFormula}
        handrerQuantidadeTotal={setQuantidadeTotal}
      />
      <div style={{ display: "none" }}>
        <FormulacaoModeloImpressao
          referencia={(el: any) => (componentRef.current = el)}
          formula={formula}
          capacidadeMisturador={capacidadeMisturador}
          fazendaSelecionada={fazendaSelecionada}
          quantidadeTotal={quantidadeTotal}
        />
      </div>
    </>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      overflowX: "auto" as const,
      padding: "46px",
      zIndex: -1,
      bottom: 0,
      top: "17%",
      minHeight: "calc(100vh - 90px)",
    },
    containerPage: {
      flexgrow: 1,
      height: "100%",
      backgroundColor: "#fff",
      width: "100%",
    },
    selectInput: {
      maxWidth: 200,
      width: "100%",
      marginLeft: 32,
    },
    titulo: {
      fontSize: 20,
      fontWeight: "bold" as const,
      fontStyle: "medium" as const,
      fontStretch: "medium" as const,
      lineHeight: "medium" as const,
      letterSpacing: "medium" as const,
      color: "#333333",
      marginTop: 40,
    },
    formContainer: {
      marginTop: 2,
    },
    inputFiltro: {
      width: 274,
    },
    linhaFiltro: {
      display: "flex" as const,
    },
    botaoSalvar: {
      borderRadius: 18,
      float: "right" as const,
      marginRight: 0,
      marginTop: 34,
      width: 200,
      height: 38,
      boxShadow: "none!important",
      fontWeight: 500,
      marginBottom: 15,
    },
    formula: {
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
  });

export default withStyles(styles)(ListagemFormulacaoPage);
